import React, { useEffect, useRef } from 'react';
import { observer } from 'mobx-react';
import SwiperClass from 'swiper/types/swiper-class';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Signal } from 'signals';
import classNames from 'classnames';

import 'swiper/swiper.scss';
import 'swiper/components/effect-coverflow/effect-coverflow.scss';

import { clamp, lerp } from '../../utils/math';

import './Slider.scss';
import { device } from '../../utils/device';

type Props = {
    className?: string,
    type: string,
    name?: string,
    readonly?: boolean,
    onlyVertical?: boolean,
    initialIndex: number,
    children?: JSX.Element[] | boolean,
    onInit?: (swiper: SwiperClass) => void,
    onClick?: (activeIndex: number) => void,
    onChange?: (activeIndex: number) => void,
    onSwipeUpMove?: (alpha: number, manual: boolean, release: boolean) => void,
    onSwipeDownMove?: (alpha: number, manual: boolean, release: boolean) => void,
};

function usePrevious<T>(value: T) {
    const ref = useRef<T>();
    useEffect(() => {
        ref.current = value;
    });
    return ref.current;
}

export default observer(function Slider({
    className,
    type,
    name,
    readonly,
    onlyVertical,
    initialIndex,
    children,
    onInit,
    onClick,
    onChange,
    onSwipeUpMove,
    onSwipeDownMove,
}: Props) {
    const sliderRef = useRef<HTMLDivElement | null>(null);
    const swiperRef = useRef<SwiperClass>();

    const prevPrevPrevSlideRef = useRef<HTMLDivElement | null>(null);
    const prevPrevSlideRef = useRef<HTMLDivElement | null>(null);
    const prevSlideRef = useRef<HTMLDivElement | null>(null);
    const currSlideRef = useRef<HTMLDivElement | null>(null);
    const nextSlideRef = useRef<HTMLDivElement | null>(null);
    const nextNextSlideRef = useRef<HTMLDivElement | null>(null);
    const nextNextNextSlideRef = useRef<HTMLDivElement | null>(null);

    const activeCardRef = useRef<HTMLDivElement | null>(null);

    const isTouchStartRef = useRef(false);
    const isTouchMoveRef = useRef(false);
    const touchStartX = useRef<number>(0);
    const touchStartY = useRef<number>(0);
    const swipeDirRef = useRef('none');

    const proxySignalRef = useRef<Signal>();

    const prevChildren = usePrevious(children);

    const handleTouchStart = (swiper: SwiperClass) => {
        document.body.style.cursor = 'grabbing';

        if (sliderRef.current) {
            sliderRef.current.style.cursor = 'grabbing';
        }

        prevPrevPrevSlideRef.current = sliderRef.current?.querySelector('.swiper-slide.swiper-slide-prev-prev-prev') || null;
        prevPrevSlideRef.current = sliderRef.current?.querySelector('.swiper-slide.swiper-slide-prev-prev') || null;
        prevSlideRef.current = sliderRef.current?.querySelector('.swiper-slide.swiper-slide-prev') || null;
        currSlideRef.current = sliderRef.current?.querySelector('.swiper-slide.swiper-slide-active') || null;
        nextSlideRef.current = sliderRef.current?.querySelector('.swiper-slide.swiper-slide-next') || null;
        nextNextSlideRef.current = sliderRef.current?.querySelector('.swiper-slide.swiper-slide-next-next') || null;
        nextNextNextSlideRef.current = sliderRef.current?.querySelector('.swiper-slide.swiper-slide-next-next-next') || null;

        activeCardRef.current = sliderRef.current?.querySelector('.swiper-slide.swiper-slide-active .slider-card') || null;

        isTouchStartRef.current = true;
        isTouchMoveRef.current = false;
        touchStartX.current = swiper.touches.currentX;
        touchStartY.current = 0;
        swipeDirRef.current = 'none';
    };

    const handleSliderMove = (swiper: SwiperClass) => {
        isTouchMoveRef.current = true;

        const delta = touchStartX.current - swiper.touches.currentX;
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        const alpha = clamp(Math.abs(delta) / swiper.slidesSizesGrid[0], 0, 1);

        if (alpha >= 1 && !(Array.isArray(children) && (swiper.activeIndex === 0 || swiper.activeIndex === children.length - 1))) {
            if (prevPrevPrevSlideRef.current) {
                prevPrevPrevSlideRef.current.style.opacity = '';
                prevPrevPrevSlideRef.current.style.transition = '';
            }
            if (prevPrevSlideRef.current) {
                prevPrevSlideRef.current.style.opacity = '';
                prevPrevSlideRef.current.style.transition = '';
            }
            if (prevSlideRef.current) {
                prevSlideRef.current.style.opacity = '';
                prevSlideRef.current.style.transition = '';
            }
            if (currSlideRef.current) {
                currSlideRef.current.style.opacity = '';
                currSlideRef.current.style.transition = '';
            }
            if (nextSlideRef.current) {
                nextSlideRef.current.style.opacity = '';
                nextSlideRef.current.style.transition = '';
            }
            if (nextNextSlideRef.current) {
                nextNextSlideRef.current.style.opacity = '';
                nextNextSlideRef.current.style.transition = '';
            }
            if (nextNextNextSlideRef.current) {
                nextNextNextSlideRef.current.style.opacity = '';
                nextNextNextSlideRef.current.style.transition = '';
            }

            if (activeCardRef.current) {
                activeCardRef.current.style.transform = '';
                activeCardRef.current.style.transition = '';
            }

            prevPrevPrevSlideRef.current = sliderRef.current?.querySelector('.swiper-slide.swiper-slide-prev-prev-prev') || null;
            prevPrevSlideRef.current = sliderRef.current?.querySelector('.swiper-slide.swiper-slide-prev-prev') || null;
            prevSlideRef.current = sliderRef.current?.querySelector('.swiper-slide.swiper-slide-prev') || null;
            currSlideRef.current = sliderRef.current?.querySelector('.swiper-slide.swiper-slide-active') || null;
            nextSlideRef.current = sliderRef.current?.querySelector('.swiper-slide.swiper-slide-next') || null;
            nextNextSlideRef.current = sliderRef.current?.querySelector('.swiper-slide.swiper-slide-next-next') || null;
            nextNextNextSlideRef.current = sliderRef.current?.querySelector('.swiper-slide.swiper-slide-next-next-next') || null;

            activeCardRef.current = sliderRef.current?.querySelector('.swiper-slide.swiper-slide-active .slider-card') || null;

            touchStartX.current = swiper.touches.currentX;

            return;
        }

        if (type === 'players' && device.isWideScreen) {
            if (delta >= 0) {
                if (prevPrevSlideRef.current) {
                    prevPrevSlideRef.current.style.opacity = `${lerp(1, 0, alpha)}`;
                    prevPrevSlideRef.current.style.transition = 'all 0ms ease 0ms';
                }
                if (nextNextNextSlideRef.current) {
                    nextNextNextSlideRef.current.style.opacity = `${lerp(0, 1, alpha)}`;
                    nextNextNextSlideRef.current.style.transition = 'all 0ms ease 0ms';
                }
            } else {
                if (prevPrevPrevSlideRef.current) {
                    prevPrevPrevSlideRef.current.style.opacity = `${lerp(0, 1, alpha)}`;
                    prevPrevPrevSlideRef.current.style.transition = 'all 0ms ease 0ms';
                }
                if (nextNextSlideRef.current) {
                    nextNextSlideRef.current.style.opacity = `${lerp(1, 0, alpha)}`;
                    nextNextSlideRef.current.style.transition = 'all 0ms ease 0ms';
                }
            }
        } else if (delta >= 0) {
            if (prevSlideRef.current) {
                prevSlideRef.current.style.opacity = `${lerp(1, 0, alpha)}`;
                prevSlideRef.current.style.transition = 'all 0ms ease 0ms';
            }
            if (nextNextSlideRef.current) {
                nextNextSlideRef.current.style.opacity = `${lerp(0, 1, alpha)}`;
                nextNextSlideRef.current.style.transition = 'all 0ms ease 0ms';
            }
        } else {
            if (prevPrevSlideRef.current) {
                prevPrevSlideRef.current.style.opacity = `${lerp(0, 1, alpha)}`;
                prevPrevSlideRef.current.style.transition = 'all 0ms ease 0ms';
            }
            if (nextSlideRef.current) {
                nextSlideRef.current.style.opacity = `${lerp(1, 0, alpha)}`;
                nextSlideRef.current.style.transition = 'all 0ms ease 0ms';
            }
        }

        if (activeCardRef.current) {
            activeCardRef.current.style.transform = `translate(-50%, -50%) scale(${lerp(1.25, 1, alpha)})`;
            activeCardRef.current.style.transition = 'all 0ms ease 0ms';
        }
    };

    const handleTouchMoveOpposite = (swiper: SwiperClass, event: TouchEvent | MouseEvent | PointerEvent) => {
        if (!event.target || !activeCardRef.current || !activeCardRef.current.parentElement) {
            return;
        }

        if (isTouchStartRef.current && !activeCardRef.current.parentElement.contains(event.target as Node)) {
            return;
        }

        isTouchMoveRef.current = true;

        if (isTouchStartRef.current) {
            isTouchStartRef.current = false;
            touchStartY.current = 'changedTouches' in event ? event.changedTouches[0].clientY : event.clientY;
        }

        const delta = touchStartY.current - ('changedTouches' in event ? event.changedTouches[0].clientY : event.clientY);

        if (swipeDirRef.current === 'none') {
            swipeDirRef.current = delta > 0 ? 'up' : (delta < 0 ? 'down' : 'none');
        }

        if (swipeDirRef.current === 'up') {
            if (onSwipeUpMove) {
                onSwipeUpMove(delta < 0 ? 0 : delta, true, false);
            }
        } else if (swipeDirRef.current === 'down') {
            if (onSwipeDownMove) {
                onSwipeDownMove(delta > 0 ? 0 : delta, true, false);
            }
        }
    };

    const handleTouchEnd = (swiper: SwiperClass, event: TouchEvent | MouseEvent | PointerEvent) => {
        document.body.style.cursor = '';

        if (sliderRef.current) {
            sliderRef.current.style.cursor = '';
        }

        if (isTouchStartRef.current) {
            isTouchStartRef.current = false;
            touchStartY.current = 'changedTouches' in event ? event.changedTouches[0].clientY : event.clientY;
        }

        const delta = touchStartY.current - ('changedTouches' in event ? event.changedTouches[0].clientY : event.clientY);

        if (swipeDirRef.current === 'none') {
            swipeDirRef.current = delta > 0 ? 'up' : (delta < 0 ? 'down' : 'none');
        }

        if (swipeDirRef.current === 'up') {
            if (onSwipeUpMove) {
                onSwipeUpMove(delta < 0 ? 0 : delta, false, true);
            }
        } else if (swipeDirRef.current === 'down') {
            if (onSwipeDownMove) {
                onSwipeDownMove(delta > 0 ? 0 : delta, false, true);
            }
        }

        isTouchStartRef.current = false;
        touchStartY.current = 0;
        swipeDirRef.current = 'none';

        //
        if (prevPrevPrevSlideRef.current) {
            prevPrevPrevSlideRef.current.style.opacity = '';
            prevPrevPrevSlideRef.current.style.transition = '';
        }
        if (prevPrevSlideRef.current) {
            prevPrevSlideRef.current.style.opacity = '';
            prevPrevSlideRef.current.style.transition = '';
        }
        if (prevSlideRef.current) {
            prevSlideRef.current.style.opacity = '';
            prevSlideRef.current.style.transition = '';
        }
        if (currSlideRef.current) {
            currSlideRef.current.style.opacity = '';
            currSlideRef.current.style.transition = '';
        }
        if (nextSlideRef.current) {
            nextSlideRef.current.style.opacity = '';
            nextSlideRef.current.style.transition = '';
        }
        if (nextNextSlideRef.current) {
            nextNextSlideRef.current.style.opacity = '';
            nextNextSlideRef.current.style.transition = '';
        }
        if (nextNextNextSlideRef.current) {
            nextNextNextSlideRef.current.style.opacity = '';
            nextNextNextSlideRef.current.style.transition = '';
        }

        if (activeCardRef.current) {
            activeCardRef.current.style.transform = '';
            activeCardRef.current.style.transition = '';
        }
    };

    const handleClick = (swiper: SwiperClass) => {
        if (swiper.clickedIndex == null) {
            return;
        }

        if (swiper.clickedIndex === swiper.activeIndex) {
            if (onClick) {
                onClick(swiper.clickedIndex);
            }

            return;
        }

        if (!onlyVertical) {
            swiper.slideTo(swiper.clickedIndex, 300);
        }
    };

    const handleChange = (swiper: SwiperClass) => {
        if (onChange) {
            onChange(swiper.activeIndex);
        }
    };

    useEffect(() => {
        proxySignalRef.current = new Signal();
        proxySignalRef.current.add((type: string, ...args) => {
            if (readonly) {
                return;
            }

            if (type === 'touch-start') {
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                handleTouchStart(...args);
            }

            if (type === 'slider-move' && !onlyVertical) {
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                handleSliderMove(...args);
            }

            if (type === 'touch-move-opposite') {
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                handleTouchMoveOpposite(...args);
            }

            if (type === 'touch-end') {
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                handleTouchEnd(...args);
            }

            if (type === 'click') {
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                handleClick(...args);
            }

            if (type === 'change') {
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                handleChange(...args);
            }
        });

        return () => {
            proxySignalRef.current?.dispose();
        };
    }, [handleTouchStart, handleSliderMove, handleTouchMoveOpposite, handleTouchEnd, handleClick]);

    useEffect(() => {
        if (!swiperRef.current) {
            return;
        }

        if (!Array.isArray(prevChildren) || !Array.isArray(children)) {
            return;
        }

        if (children.every((child, i) => child.key === prevChildren[i]?.key)) {
            return;
        }

        swiperRef.current.$wrapperEl.removeClass('slider-init');

        swiperRef.current.update();
        swiperRef.current.slideTo(initialIndex, 0);

        setTimeout(() => swiperRef.current?.$wrapperEl?.addClass('slider-init'), 16);

        if (onChange) {
            onChange(swiperRef.current.activeIndex);
        }
    }, [prevChildren, children, name, type, initialIndex]);

    return (
        <div className={classNames(['slider', className])} ref={sliderRef}>
            {!readonly && !onlyVertical && (
                <div
                    style={{
                        position: 'absolute',
                        top: 0,
                        right: '-100vw',
                        width: '200vw',
                        height: '100%',
                        zIndex: -1,
                    }}
                    onClick={() => {
                        if (Array.isArray(children) && swiperRef.current && (swiperRef.current.activeIndex >= children.length - 1)) {
                            swiperRef.current.slideTo(initialIndex, 300);
                        }
                    }}
                    role="presentation"
                />
            )}

            {Array.isArray(children) && (
                <Swiper
                    initialSlide={initialIndex}
                    centeredSlides
                    watchSlidesProgress
                    slidesPerView="auto"
                    threshold={2}
                    zoom={false}
                    allowTouchMove={!readonly}
                    allowSlidePrev={!readonly && !onlyVertical}
                    allowSlideNext={!readonly && !onlyVertical}
                    updateOnWindowResize={false}
                    onInit={(swiper) => {
                        swiperRef.current = swiper;

                        if (onInit) {
                            onInit(swiper);
                        }

                        setTimeout(() => swiper.$wrapperEl?.addClass('slider-init'), 16);
                    }}
                    onResize={(swiper) => {
                        swiper.$wrapperEl.removeClass('slider-init');
                        swiper.update();

                        setTimeout(() => {
                            swiper.$wrapperEl?.addClass('slider-init');
                            swiper.update();
                        }, 16);
                    }}
                    onTouchStart={(...args) => proxySignalRef.current?.dispatch('touch-start', ...args)}
                    onSliderMove={(...args) => proxySignalRef.current?.dispatch('slider-move', ...args)}
                    onTouchMoveOpposite={(...args) => proxySignalRef.current?.dispatch('touch-move-opposite', ...args)}
                    onTouchEnd={(...args) => proxySignalRef.current?.dispatch('touch-end', ...args)}
                    onTouchCancel={(...args) => proxySignalRef.current?.dispatch('touch-end', ...args)}
                    onClick={(...args) => proxySignalRef.current?.dispatch('click', ...args)}
                    // onSlideChange={(...args) => proxySignalRef.current?.dispatch('change', ...args)}
                    onSlideChange={handleChange}
                >
                    {(Array.isArray(children) ? children : [children]).map(child => (
                        <SwiperSlide key={child.key}>
                            <div className="slider-card">
                                <div className="slider-card--body">
                                    {child}
                                </div>
                            </div>
                        </SwiperSlide>
                    ))}
                </Swiper>
            )}
        </div>
    );
});
